/**
 * The merchant timezone kernel — i18n-free on purpose.
 *
 * "What day is it for the operator?" is the single fact behind every date the
 * console shows and every date filter it sends. The backend owns it:
 * `/api/public/system/timezone` serves the deployment default (before
 * sign-in), and `/api/system/timezone` serves the session tenant's value
 * (after sign-in). The console bootstraps from the matching endpoint via
 * `MerchantTimezoneContext` and caches the result in `localStorage` so a cold
 * load does not flash a UTC date. `DEFAULT_MERCHANT_TZ` is only the initial
 * value until the first response arrives.
 *
 * This module holds the zone itself — the STATE. The stateless arithmetic over
 * it moved into `@resource-kit/controllers` (#2167, ADR-0035 D1);
 * `merchantWallTimeToUtcIso` is the two-line binding of that arithmetic to
 * this app's zone. #2170 removed the `tzOffsetMsAt` re-export that stood here
 * during the migration: a host path in front of a package symbol is a shim
 * whichever statement mints it, and nothing in this app imported it. Callers
 * that want the raw arithmetic take it from the package. `datetime.ts` holds
 * the LOCALE-aware rendering built on top and re-exports the zone state here,
 * so existing importers are unaffected.
 *
 * Nothing in this file is user-facing, so the fixed `en-US` locale below is a
 * format choice rather than locale policy: it pins ASCII digits and a
 * parseable field order. The repo's ban on a hardcoded locale is about text an
 * operator READS.
 */
import { calendarDayIn, wallTimeToUtcIso } from '@resource-kit/controllers';

export const DEFAULT_MERCHANT_TZ = 'America/Los_Angeles';
const STORAGE_KEY = 'merchantTimezone';

let cachedTz: string | null = null;

function isValidIanaTimezone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Read the active merchant timezone. Order of preference:
 *   1. In-memory cache (set by `setMerchantTz` on bootstrap)
 *   2. localStorage snapshot (last known good value)
 *   3. Hardcoded default
 */
export function getMerchantTz(): string {
  if (cachedTz) return cachedTz;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidIanaTimezone(stored)) {
      cachedTz = stored;
      return stored;
    }
  } catch {
    /* SSR / private mode — fall through */
  }
  return DEFAULT_MERCHANT_TZ;
}

/**
 * Persist the merchant timezone resolved from the backend. Updates
 * the in-memory cache + localStorage snapshot.
 */
export function setMerchantTz(tz: string): void {
  if (!isValidIanaTimezone(tz)) {
    console.warn('[merchant-tz] Refusing to set invalid timezone:', tz);
    return;
  }
  cachedTz = tz;
  try {
    localStorage.setItem(STORAGE_KEY, tz);
  } catch {
    /* localStorage unavailable */
  }
}

/**
 * Today's calendar date (`YYYY-MM-DD`) in the merchant timezone, read from
 * the clock at the moment of the call — the kit's `calendarDayIn` bound to
 * the zone this module owns (the same binding pattern as
 * `merchantWallTimeToUtcIso` below).
 *
 * For NON-render readers only: event handlers and `queryFn`s, which run at
 * the moment that matters and cannot call a hook. A component that shows or
 * requests "today" while it renders reads `useToday()` from
 * `@resource-kit/controllers/react` instead: that hook is subscribed, so it
 * moves to the new day at merchant midnight together with every list (#2786).
 */
export function todayInMerchantTz(now: Date = new Date()): string {
  return calendarDayIn(getMerchantTz(), now);
}

/**
 * The UTC instant a merchant-local WALL CLOCK reading names, as an ISO-8601
 * string — {@link wallTimeToUtcIso} bound to the zone this module owns.
 *
 * The binding is the whole point of the split: the arithmetic is pure and
 * lives in the package, the ZONE is state and lives here, and there is exactly
 * one place (this line) where the two meet for callers that do not carry a
 * zone of their own.
 */
export function merchantWallTimeToUtcIso(wall: string): string {
  return wallTimeToUtcIso(wall, getMerchantTz());
}

/**
 * The UTC instant at which the merchant calendar day `day` (YYYY-MM-DD) began,
 * as an ISO-8601 string — the lower bound for that day against a UTC
 * `created_at` column.
 *
 * Comparing against UTC midnight instead is not a rounding error but a wrong
 * answer: for `America/Los_Angeles` it sits 7-8 hours early, so the previous
 * merchant evening (17:00-24:00 local) counts as "today" for the whole day.
 */
export function merchantDayStartUtc(day: string): string {
  return merchantWallTimeToUtcIso(`${day}T00:00:00.000`);
}
